// #region License

/**
 * @license
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

// #endregion

import * as React from "react";

import { Transition } from "semantic-ui-react";

import { useConst, useEventCallback } from "@mairistem/tools";

import {
	ApplicationPageContext,
	type ApplicationPageContextProps,
} from "../ApplicationPage";
import ApplicationSessionContext from "../ApplicationSession";

import { ApplicationLoader } from "../ApplicationLoader";

import { useState } from "../../hooks";
import { applicationUserToApplicationModuleUser } from "../../utils/user";

type Components = ApplicationPageContextProps["manifest"]["components"];
type Component = Components extends Record<string, infer T> ? T : never;

export type ApplicationModuleProps = {
	[key: string]: unknown;

	/** */
	name: string;

	/** */
	code: string;

	/** */
	scope: "content" | "header" | "footer" | "help";

	/** */
	container: React.MutableRefObject<HTMLElement>;
};

export type ApplicationModuleType = React.ComponentType<ApplicationModuleProps>;

export const ApplicationModule: ApplicationModuleType = (
	props: ApplicationModuleProps,
) => {
	const { name, code, scope, container } = props;

	const root = container.current;

	const { manifest, loading, error } = React.useContext(ApplicationPageContext);
	const { user } = React.useContext(ApplicationSessionContext);

	const [component, setComponent] = useState<Component>();

	const [render, setRender] = React.useState(false);
	const [timed, setTimed] = React.useState(false);
	const [failed, setFailed] = React.useState(false);

	const exception = React.useRef(null);

	const timer = React.useRef(null);
	const failure = React.useRef(null);

	// eslint-disable-next-line no-shadow, @typescript-eslint/no-shadow
	const routes = useConst(
		manifest?.menus?.map(({ code, name, path }) => ({ code, name, path })),
	);

	const catchException = useEventCallback(({ message }) => {
		// eslint-disable-next-line no-console
		console.error(message);

		exception.current = message;
	});

	React.useEffect(() => {
		if (manifest && name) {
			React.startTransition(() => {
				setComponent(manifest.components?.[name]);
			});
		}
	}, [manifest, name]);

	React.useEffect(() => {
		if (component && root) {
			component.mount(root, {
				scope,
				module: { routes },
				user: applicationUserToApplicationModuleUser(user)
			});

			timer.current = setTimeout(() => setTimed(true), 30000);
		}

		return () => {
			if (component) {
				component.unmount(root);

				clearTimeout(timer.current);
			}

			React.startTransition(() => {
				setRender(false);
				setTimed(false);
			});

			exception.current = null;
		};
	}, [root, code, scope, component, user]);

	// Au rechargement, le module courant est résolu en plusieurs passes (store
	// Redux pré-alimenté puis rempli), ce qui fait transiter par un état d'erreur
	// avant que le chargement final n'aboutisse. On ne considère l'échec comme réel
	// que si le module n'a produit aucun composant et n'est pas rendu : dès qu'il
	// est affiché, un `error` résiduel (issu d'une passe périmée) est ignoré. Le
	// court délai de grâce absorbe en plus les erreurs transitoires.
	React.useEffect(() => {
		if (error && !loading && !component && !render) {
			failure.current = setTimeout(() => setFailed(true), 1000);
		} else {
			clearTimeout(failure.current);
			setFailed(false);
		}

		return () => clearTimeout(failure.current);
	}, [error, loading, component, render]);

	React.useEffect(() => {
		if (timed) {
			throw new Error(
				"Le chargement du module n'a pas répondu dans la limite de temps impartie.",
			);
		}
		if (failed) {
			throw new Error("Le chargement du module a échoué.");
		}
	}, [failed, timed]);

	React.useEffect(() => {
		if (!render) {
			if (exception.current) {
				throw new Error(exception.current);
			}
		}
	}, [render]);

	React.useLayoutEffect(() => {
		React.startTransition(() => {
			setRender(false);
		});

		const observer = new MutationObserver(() => {
			clearTimeout(timer.current);

			React.startTransition(() => {
				setRender(!!root.childNodes.length);
			});
		});

		if (root) {
			observer.observe(root, {
				childList: true,
			});
		}

		window.addEventListener("error", catchException);

		return () => {
			window.removeEventListener("error", catchException);

			if (root) {
				observer.disconnect();
			}
		};
	}, [root]);

	return (
		<Transition visible={!render} animation="fade" unmountOnHide>
			<ApplicationLoader type={scope === "content" && "overlay"} />
		</Transition>
	);
};
